import {
  ConnectedPosition,
  ConnectionPositionPair,
  OverlayContainer,
} from '@angular/cdk/overlay';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  Signal,
  TemplateRef,
  ViewChild,
  computed,
  inject,
  input,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import {
  GoogleMap,
  MapAdvancedMarker,
  MapInfoWindow,
  MapPolygon,
  MapPolyline,
} from '@angular/google-maps';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  Bounds,
  DEFAULT_POLYGON_FILL_COLOR,
  DEFAULT_POLYGON_FILL_OPACITY,
  DEFAULT_POLYGON_STROKE_COLOR,
  DEFAULT_POLYGON_STROKE_WEIGHT,
  MapOverlay,
  Marker,
  MarkerInfoWindowContext,
  Polygon,
  PolygonInfoWindowContext,
  Polyline,
  Svg,
  SvgOverlay,
  baseMapOptions,
  createMapOverlayClass,
  createSvgOverlayClass,
} from '../../models/map';
import { IconComponent } from '../icons/components/icon/icon.component';
import { SymbolComponent } from '../icons/components/symbol/symbol.component';
import { MatSymbol } from '../icons/models/mat-symbol.interface';
import { defaultDropdownPositionPairs } from '../overlay/cdk-overlay-position-pairs';
import { TimelineChartV2Component } from '../timeline-chart/components/timeline-chart-v2/timeline-chart-v2.component';
import {
  AmLineChartItem,
  TimelineChartInput,
  TimelineChartInputV2,
  isTimelineChartInputV2,
} from '../timeline-chart/models/timeline-chart-input.interface';
import { TimelineChartComponent } from '../timeline-chart/timeline-chart.component';
import { GoogleMapsLoaderService } from './google-maps-loader.service';
import { ControlTemplatesComponent } from './components/control-templates/control-templates.component';
import {
  GoogleMapsControlPosition,
  GoogleMapsControlType,
  GoogleMapsInput,
  GoogleMapsLayerOption,
  GoogleMapsScaleControlInput,
  MapTypeId,
} from './model/google-maps-input.interface';

/**
 * Element-wise identity comparison, used as the `equal` of the drawing computeds. Their
 * sources re-allocate the array (a `.filter()`, or a `?? []` fallback) while the drawing
 * objects themselves stay identical, so without this every read re-notifies everything
 * derived from them.
 */
const areArraysIdentityEqual = <T>(a: readonly T[], b: readonly T[]): boolean =>
  a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * Height assumed for an embedded timeline until the chart reports its own. It is a *fallback*,
 * not the v1 height: `fitBounds()` runs from `onMapInitialized`, before the v2 chart's
 * `(sizeChanged)` has fired, so this is the only number available at that moment — and the v1
 * chart never reports a size at all, so it keeps this value for good.
 */
const DEFAULT_TIMELINE_HEIGHT = 220;

/** Gap the bottom control corners leave above the timeline they float over. */
const BOTTOM_CONTROLS_GAP = 8;

/**
 * `pixelOffset` height for the marker info window — half the 32px marker badge, matching the
 * `gmp-advanced-marker` centring offset in the stylesheet.
 */
const MARKER_INFO_WINDOW_OFFSET_Y = 16;

/** Fallback badge glyph for a marker whose payload carries no usable `matsymbol`. */
const DEFAULT_MARKER_SYMBOL: MatSymbol = { symbol: 'location_on', fill: true };

/**
 * The badge glyph for one marker. A module-local pure function rather than a component method
 * because the template must not call it: a method call in a binding is re-evaluated on every
 * change-detection pass and returns a fresh object each time, which kept `pozi-symbol`'s
 * `input.required<MatSymbol>()` permanently dirty once per marker per pass. `markerViews` calls
 * it once per marker per *payload* instead.
 */
const markerSymbol = (matsymbol: MatSymbol | null | undefined): MatSymbol =>
  matsymbol?.symbol
    ? { ...matsymbol, fill: matsymbol.fill ?? true }
    : DEFAULT_MARKER_SYMBOL;

/**
 * One marker paired with the `MatSymbol` its badge renders, so the template reads a stable object
 * instead of calling a factory. Mirrors what T1.1 did for `tile.component`.
 */
export interface MarkerView<TInfo = unknown> {
  marker: Marker<TInfo>;
  symbol: MatSymbol;
}

@Component({
  selector: 'pozi-google-maps',
  imports: [
    GoogleMap,
    NgTemplateOutlet,
    MapAdvancedMarker,
    MapInfoWindow,
    MapPolyline,
    MapPolygon,
    TimelineChartComponent,
    TimelineChartV2Component,
    ControlTemplatesComponent,
    IconComponent,
    SymbolComponent,
  ],
  providers: [DatePipe],
  templateUrl: './google-maps.component.html',
  styleUrl: './google-maps.component.scss',
})
export class GoogleMapsComponent<TInfo = unknown> implements OnDestroy {
  private googleMapsLoader = inject(GoogleMapsLoaderService);
  private datePipe = inject(DatePipe);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private overlayContainer = inject(OverlayContainer);
  private hostElement = inject(ElementRef);

  /**
   * Every info window in the template — the one per marker *and* the shared polygon one, since
   * signal view queries traverse `@for` / `@if` blocks. `closeInfoWindows()` is the only
   * consumer. It replaces an `@ViewChild('infoWindow')` that resolved to marker *zero*: the
   * `#infoWindow` ref is declared inside the marker `@for`, so it is out of scope at the
   * `(mapClick)` binding and the expression there read the class field instead — which is why a
   * map click closed only the first marker's window.
   */
  readonly infoWindows = viewChildren(MapInfoWindow);
  /**
   * The one info window every marker shares. A signal query rather than a template ref passed
   * down from the marker `@for`: the window sits inside its own `@if`, and a ref declared in one
   * embedded view is out of scope in a sibling one — the mistake that made a map click close
   * marker zero only (see `infoWindows` above).
   */
  readonly markerInfoWindow = viewChild<MapInfoWindow>('markerInfoWindow');
  @ViewChild('polygonInfoWindow') polygonInfoWindow: MapInfoWindow | undefined;
  @ViewChild('mapContainer') mapContainer: ElementRef | undefined;

  /**
   * `this`, so the control-templates child can be handed the component whose state its templates
   * read. A plain field rather than `[host]="this"` in the template, which the compiler does not
   * accept as an expression.
   */
  readonly self: GoogleMapsComponent<TInfo> = this;

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  @HostListener('document:mozfullscreenchange')
  @HostListener('document:MSFullscreenChange')
  onFullscreenChange() {
    this.updateOverlayContainerPosition();
  }

  input = input<GoogleMapsInput<TInfo>>({});
  isBackToOverviewButtonEnabled = input<boolean>(true);
  /**
   * One template for **all** the markers, stamped with each marker as `$implicit`. It is a
   * component input rather than a `Marker` field because both consumers only ever wanted one
   * template for the whole payload — `GoogleMapsService.updateMarkersWithSameInfoWindowTemplate`
   * existed to copy it onto every marker, and mohu incident-details built the same object literal
   * by hand. Omit it and no marker is clickable and no SDK `InfoWindow` is built at all.
   */
  infoWindowTemplate = input<
    TemplateRef<MarkerInfoWindowContext<TInfo>> | undefined
  >();
  /** Stamped with `{ polygon, polygonInfoWindow }` — both named, no `$implicit`. */
  polygonInfoWindowTemplate = input<
    TemplateRef<PolygonInfoWindowContext> | undefined
  >();

  mapTypeIds = input<Record<MapTypeId | string, string>>({
    roadmap: $localize`Default`,
    hybrid: $localize`Satellite`,
  });

  mapControlPositions = input<
    Record<GoogleMapsControlPosition, GoogleMapsControlType[]>
  >({
    [GoogleMapsControlPosition.TOP_LEFT]: ['title'],
    [GoogleMapsControlPosition.TOP_RIGHT]: ['mapType', 'routerLink'],
    [GoogleMapsControlPosition.BOTTOM_LEFT]: [
      'routeSpeed',
      'scale',
      'minimizedTimeline',
    ],
    [GoogleMapsControlPosition.BOTTOM_RIGHT]: ['layers', 'fullScreen', 'zoom'],
  });

  /** Both records are iterated in declaration order — no differ, no alphabetization. */
  readonly mapTypeEntries = computed<{ key: string; label: string }[]>(() =>
    Object.entries(this.mapTypeIds()).map(([key, label]) => ({ key, label }))
  );
  readonly controlPositionEntries = computed<
    { key: GoogleMapsControlPosition; controls: GoogleMapsControlType[] }[]
  >(() =>
    Object.entries(this.mapControlPositions()).map(([key, controls]) => ({
      key: key as GoogleMapsControlPosition,
      controls,
    }))
  );

  /**
   * The nine control templates, or `null` while the child that declares them has not been
   * created yet — which is the case on the first change-detection pass, since the corners that
   * consume them sit above the child's element. This replaces an `ngAfterViewInit` whose only job
   * was to copy nine `@ViewChild` TemplateRefs into a signal.
   */
  private readonly controlTemplateHost = viewChild(ControlTemplatesComponent);
  readonly controlTemplates = computed<Record<
    GoogleMapsControlType,
    TemplateRef<unknown>
  > | null>(() => this.controlTemplateHost()?.templates() ?? null);

  scaleControlInput = signal<GoogleMapsScaleControlInput | null>(null);

  readonly mapTypeId: Signal<MapTypeId | undefined> = computed(() => {
    return this.input()?.mapTypeId;
  });
  readonly hasTitle: Signal<boolean> = computed(() => {
    return !!this.input()?.title;
  });
  readonly markers: Signal<Marker<TInfo>[]> = computed(
    () => {
      return (
        this.input()?.drawings?.markers ??
        (this.markerFromTimeline() ? [this.markerFromTimeline()!] : [])
      );
    },
    { equal: areArraysIdentityEqual }
  );
  /**
   * What the marker `@for` iterates. The badge symbol is derived **here**, once per payload,
   * rather than by a `getMarkerSymbol(marker.matsymbol)` call in the binding: a template method
   * call is not memoized by the compiler (unlike an inline object literal, which it wraps in
   * `ɵɵpureFunctionN`), so it returned a fresh `MatSymbol` on every change-detection pass and
   * re-fired `pozi-symbol`'s `input.required<MatSymbol>()` once per marker per pass. `markers()`
   * carries the identity `equal`, so this recomputes only when the marker list really changes and
   * every pass in between reads the same objects.
   */
  readonly markerViews: Signal<MarkerView<TInfo>[]> = computed(() =>
    this.markers().map((marker: Marker<TInfo>) => ({
      marker,
      symbol: markerSymbol(marker.matsymbol),
    }))
  );
  readonly polygons: Signal<Polygon[]> = computed(
    () => {
      return this.input()?.drawings?.polygons ?? [];
    },
    { equal: areArraysIdentityEqual }
  );
  readonly polylines: Signal<Polyline[]> = computed(
    () => {
      return this.input()?.drawings?.polylines ?? [];
    },
    { equal: areArraysIdentityEqual }
  );
  /**
   * `bounds` is optional-chained even though `Svg.bounds` is typed required. Guarding the two
   * corners already conceded that the backend sends partial bounds; an entry omitting `bounds`
   * altogether used to throw a `TypeError` *inside the computed*, so every reader of `svgs()` —
   * `hasRouteSpeedControl`, `isDrawingsEmpty`, `fitBounds`, the template — threw with it and the
   * whole map view went down instead of one route being dropped.
   *
   * This filter is also the only thing that makes the unguarded `svg.bounds.min` / `.max` reads
   * in `fitBounds()` and `createSvgOverlay()` safe: both iterate `svgs()` and nothing else, so a
   * bounds-less entry never reaches them.
   */
  readonly svgs: Signal<Svg[]> = computed(
    () => {
      return (
        this.input()?.drawings?.svgs?.filter(
          (svg: Svg) => !!svg.svg && !!svg.bounds?.max && !!svg.bounds?.min
        ) ?? []
      );
    },
    { equal: areArraysIdentityEqual }
  );
  /**
   * Whether to build the shared marker info window at all. **Both** terms matter: a consumer that
   * passes no template gets no SDK `InfoWindow` (every map tile, and the session map's timeline
   * pin), and neither does a payload with no markers to anchor one to. Before C3 this read
   * `markers().some(m => !!m.infoWindowTemplate)`, which had the same two effects because the
   * per-marker template was only ever set for every marker at once.
   */
  readonly hasMarkerInfoWindow: Signal<boolean> = computed(
    () => !!this.infoWindowTemplate() && this.markers().length > 0
  );
  readonly bounds: Signal<Bounds | undefined> = computed(() => {
    return this.input()?.drawings?.bounds;
  });
  readonly isDrawingsEmpty: Signal<boolean> = computed(() => {
    return (
      this.markers().length +
        this.polygons().length +
        this.polylines().length +
        this.svgs().length ===
        0 && !this.bounds()
    );
  });

  // Controls
  readonly hasMapTypeControl: Signal<boolean> = computed(() => {
    return !!this.input()?.controls?.mapType;
  });
  readonly hasFullScreenControl: Signal<boolean> = computed(() => {
    return !!this.input()?.controls?.fullScreen;
  });
  readonly hasZoomControl: Signal<boolean> = computed(() => {
    return !!this.input()?.controls?.zoom;
  });
  readonly layers: Signal<GoogleMapsLayerOption[]> = computed(() => {
    return this.input()?.controls?.layers ?? [];
  });
  readonly hasRouteSpeedControl: Signal<boolean> = computed(() => {
    return (
      !!this.input()?.controls?.routeSpeed &&
      this.svgs().some((svg, _, svgs) => {
        if (!svg.layer) return false;

        const sameLayerSvgs = svgs.filter((s) => s.layer === svg.layer);

        return (
          sameLayerSvgs.some((s) => !!this.isSvgRouteColoredBySpeed(s)) &&
          sameLayerSvgs.some((s) => !this.isSvgRouteColoredBySpeed(s))
        );
      })
    );
  });
  readonly hasScaleControl: Signal<boolean> = computed(() => {
    return !!this.input()?.controls?.scale;
  });

  //Map Options
  readonly defaultMapOptions = computed<google.maps.MapOptions>(() => ({
    ...baseMapOptions,
    mapTypeId: this.mapTypeId(),
    center: { lat: 47.162494, lng: 19.503304 },
    zoom: 7,
  }));

  mapOptionsOverride = input<google.maps.MapOptions | undefined>();

  mapOptions = computed<google.maps.MapOptions>(() => ({
    ...this.defaultMapOptions(),
    ...this.mapOptionsOverride(),
  }));

  // RouterLink
  readonly routerLink = computed<string | string[] | null | undefined>(() => {
    return this.input().routerLink;
  });

  readonly queryParams = computed<Params | null | undefined>(() => {
    return this.input().queryParams;
  });

  readonly isOnlyOneMarkerPresent = computed<boolean>(() => {
    return (
      this.svgs().length + this.polylines().length + this.polygons().length ===
        0 && this.markers().length === 1
    );
  });

  isLayerSelectorDropdownOpen = signal(false);

  markerFromTimeline = signal<Marker<TInfo> | null>(null);

  isProgrammaticChange = false; // to distinguish user interaction
  freeRoam = signal<boolean>(false);

  map: google.maps.Map | undefined;
  mapBounds: google.maps.LatLngBounds | undefined;

  /**
   * `pixelOffset` for the shared marker info window, as a **real** `google.maps.Size` — which
   * can only be constructed once the SDK is loaded, hence a signal filled at map init rather
   * than a field initialiser. It stood in for one with a `{ height, width, equals: () => false }`
   * literal before. Still undefined on the first pass, which `InfoWindowOptions` allows; the
   * wrapper re-applies options through `setOptions` on every new reference, so the offset lands
   * well before anything can be opened.
   */
  readonly markerInfoWindowOffset = signal<google.maps.Size | undefined>(
    undefined
  );

  /**
   * Reactive mirrors of the two pieces of `map` state the template needs. `map` itself is a
   * plain field, so reading it from the template only ever updated by accident, when some
   * other event dirtied the view.
   */
  readonly mapReady = signal<boolean>(false);
  readonly currentMapTypeId = signal<string | undefined>(undefined);
  /** Every control tints itself over a non-roadmap (satellite/hybrid) base map. */
  readonly isBlurred = computed<boolean>(
    () => this.currentMapTypeId() !== 'roadmap'
  );

  layerSelectorCdkOverlayPositionPairs = computed<ConnectionPositionPair[]>(
    () => {
      const position = this.getLayersControlPosition();
      return position?.includes('bottom')
        ? this.switchTopAndBottomPositionPairs()
        : defaultDropdownPositionPairs;
    }
  );

  selectedLayers = signal<string[]>([]);

  /**
   * Keyed by the `Svg` it renders, so each payload entry owns exactly one overlay for the life
   * of the component and its markup is parsed at most once. Toggling a layer or the route-speed
   * variant is then a `setMap` on a cached overlay rather than a rebuild. It also replaces an
   * invariant that used to be held implicitly — the old array was indexed by the *filtered*
   * position, a filter three call sites each had to repeat identically.
   */
  private readonly svgOverlays = new Map<Svg, SvgOverlay>();
  /** Captured once at map init: it depends only on whether the payload carries polygons. */
  private svgMapPanesKey: keyof google.maps.MapPanes = 'overlayLayer';
  mapOverlay: MapOverlay | undefined;
  private mapEventListeners: google.maps.MapsEventListener[] = [];

  timelineHeight = signal<number>(0);

  // TODO: Remove when there is only one version to maintain
  isTimelineChartInputV2 = isTimelineChartInputV2;
  timelineV1 = computed(() => {
    return this.input().timeline as TimelineChartInput;
  });
  timelineV2 = computed(() => {
    return this.input().timeline as TimelineChartInputV2;
  });
  isTimelineDisplayed = signal<boolean>(true);

  /**
   * How far the bottom control corners sit above the timeline, as the `--timeline-height` custom
   * property they are bound to. Only the v2 chart reports its own height; everything else falls
   * back to the same constant `getBoundsPadding()` reserves, which is where the `228px` this
   * replaces came from — `220 + 8`, with nothing in the code connecting the two numbers.
   */
  readonly bottomControlsOffset = computed<string>(() => {
    const timeline = this.input().timeline;
    const height =
      timeline && isTimelineChartInputV2(timeline)
        ? this.timelineHeight()
        : DEFAULT_TIMELINE_HEIGHT;

    return `${height + BOTTOM_CONTROLS_GAP}px`;
  });

  isRouteSpeedControlEnabled = signal<boolean>(false);
  private readonly routeSpeedControlUrlParam = 'routeSpeedControl';
  private readonly isRouteColoredBySpeedPropertyName = 'isRouteColoredBySpeed';

  readonly defaultPolygonStrokeColor = DEFAULT_POLYGON_STROKE_COLOR;
  readonly defaultPolygonStrokeWeight = DEFAULT_POLYGON_STROKE_WEIGHT;
  readonly defaultPolygonFillColor = DEFAULT_POLYGON_FILL_COLOR;
  readonly defaultPolygonFillOpacity = DEFAULT_POLYGON_FILL_OPACITY;

  constructor() {
    this.googleMapsLoader.load();
  }

  onMapInitialized(map: google.maps.Map) {
    this.map = map;
    this.mapReady.set(true);
    this.trackMapTypeId(map);
    this.markerInfoWindowOffset.set(
      new google.maps.Size(0, MARKER_INFO_WINDOW_OFFSET_Y)
    );

    this.appendTransparentMapOverlay();

    /*
     * State before DOM. `syncSvgOverlays()` reads `selectedLayers()` and
     * `isRouteSpeedControlEnabled()`, so both have to be settled before the one pass that
     * builds the overlays — the two used to be interleaved, which made `initRouteSpeedControl`
     * discard and rebuild everything the first pass had just created.
     */
    this.initLayers();
    this.initRouteSpeedControlState();

    /* To ensure drawing svgs above polygons */
    this.svgMapPanesKey = this.polygons().length
      ? 'markerLayer'
      : 'overlayLayer';
    this.syncSvgOverlays();

    if (!this.isDrawingsEmpty()) {
      this.adjustMapView();
    }
    this.updateSvgPathStrokeWidth();
    if (this.isBackToOverviewButtonEnabled()) {
      this.addEventListeners();
    }
  }

  /**
   * Seeds `currentMapTypeId` and keeps it in step with the SDK — the payload can arrive with
   * `mapTypeId: 'satellite'`, and the type can change from either the control or the SDK.
   * The listener is tracked, so `teardownMap()` removes it.
   */
  private trackMapTypeId(map: google.maps.Map): void {
    this.currentMapTypeId.set(map.getMapTypeId());
    this.mapEventListeners.push(
      map.addListener('maptypeid_changed', () =>
        this.currentMapTypeId.set(map.getMapTypeId())
      )
    );
  }

  initLayers() {
    this.selectedLayers.set(
      this.layers()
        .filter((layer: GoogleMapsLayerOption) => layer.isSelected)
        .map((layer: GoogleMapsLayerOption) => layer.key)
    );
    this.updateLayersInUrl(this.selectedLayers());
  }

  appendTransparentMapOverlay() {
    if (this.map && !this.input().grayscale) {
      // setMap(null), not onRemove(): the latter detaches the DOM but leaves the overlay
      // registered on the map, so the SDK keeps calling draw() on it for the rest of the page.
      this.mapOverlay?.setMap(null);
      const MapOverlay = createMapOverlayClass();
      this.mapOverlay = new MapOverlay(this.map);
    }
  }

  updateLayersInUrl(layers: string[]): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        layers: layers.length ? layers : null,
      },
      queryParamsHandling: 'merge',
    });
  }

  onLayerClick(layerKey: string): void {
    // A new array, not a mutated one: this used to `push`/`splice` the array it read and set the
    // same reference back, which `Object.is` equality rejects — so the signal never notified and
    // the check marks updated only because the click itself dirtied the view.
    this.selectedLayers.update((layers: string[]) =>
      layers.includes(layerKey)
        ? layers.filter((layer: string) => layer !== layerKey)
        : [...layers, layerKey]
    );
    this.syncSvgOverlays();
    this.updateLayersInUrl(this.selectedLayers());
  }

  zoomIn() {
    if (this.map) {
      this.map.setZoom(this.map.getZoom()! + 1);
    }
  }

  zoomOut() {
    if (this.map) {
      this.map.setZoom(this.map.getZoom()! - 1);
    }
  }

  /**
   * Sets --svg-path-stroke-width to be used in the scss.
   * The stroke-width of the SVG's path (polyline) is static. Therefore it becomes small, then eventually disappears uppon zooming out.
   * To counter this, we dinamically overwrite the stroke-width's value based on the zoom level.
   * Zoom level ranges from 0 to 22.
   * We raise to the power 2, to double the value according to zoom level. Then divide by 3, reduce the differences.
   *
   * Written on **this component's host**, not on `document.documentElement`: a tile grid renders
   * one `google.maps.Map` per map tile, each at its own zoom, and a document-level property meant
   * whichever map drew last won — every other one rendered its route at the wrong stroke width.
   * The SVG panes are descendants of the host, so the custom property still inherits down to them.
   * (Unrelated to `MapOverlay`'s cached viewport size, which reads `documentElement` on purpose.)
   *
   * The host and not `mapContainer`: `onMapInitialized` can fire *inside* the first change
   * detection pass — once the SDK is loaded, `GoogleMap.ngOnInit` takes its synchronous branch —
   * and the non-static `@ViewChild` is only assigned at the end of that pass.
   */
  updateSvgPathStrokeWidth(): void {
    const strokeWidth = Math.pow(2, 22 - this.map!.getZoom()!) / 3;
    this.hostElement.nativeElement.style.setProperty(
      '--svg-path-stroke-width',
      strokeWidth.toString()
    );
  }

  setMapTypeId(mapTypeId: string) {
    if (this.map) {
      this.map.setMapTypeId(mapTypeId);
      // The tint differs per map type, and that is the only thing that has to change — the
      // overlay used to be thrown away and rebuilt for it.
      this.mapOverlay?.setMapTypeOpacity();
    }
  }

  /**
   * Re-anchors the one shared window onto the clicked marker and swaps in that marker's own
   * content div — `setContent` takes a single element, so each marker still needs its own.
   * The wrapper's `open()` re-anchors without a `close()` first, but it is a **no-op for the
   * same anchor**, so clicking the open marker again leaves the window as it is.
   *
   * Guarded on the SDK object, not just the wrapper: `open()` asserts initialization and throws
   * in dev mode while `infoWindow` is still undefined.
   */
  openInfoWindow(marker: MapAdvancedMarker, infoWindowContent: HTMLElement) {
    const infoWindow = this.markerInfoWindow();
    if (this.infoWindowTemplate() && marker && infoWindow?.infoWindow) {
      infoWindow.open(marker, false, infoWindowContent);
    }
  }

  /**
   * The polygon window has no anchor, so it is positioned at the click instead. `latLng` is typed
   * nullable by the SDK and the wrapper's `position` setter is not, so a click without one opens
   * nothing rather than positioning the bubble at `null` — which is what the previous `event: any`
   * hid. In practice a `click` on a `Polygon` always carries one.
   */
  openPolygonInfoWindow(
    event: google.maps.PolyMouseEvent,
    infoWindowContent: HTMLElement
  ) {
    if (
      this.polygonInfoWindowTemplate() &&
      this.polygonInfoWindow?.infoWindow &&
      event.latLng
    ) {
      this.polygonInfoWindow.position = event.latLng;
      this.polygonInfoWindow.open(undefined, false, infoWindowContent);
    }
  }

  /**
   * Closes every open info window — one call for the markers' and the polygon's alike.
   *
   * The guard is not defensive padding: `MapInfoWindow.close()` calls `_assertInitialized()`,
   * which *throws* in dev mode while `infoWindow` is still undefined. The wrapper only builds it
   * once the SDK resolves, so clicking the map during SDK load used to be able to blow up.
   */
  closeInfoWindows(): void {
    this.infoWindows().forEach((infoWindow: MapInfoWindow) => {
      if (infoWindow.infoWindow) {
        infoWindow.close();
      }
    });
  }

  onBackToOverviewButtonClick(): void {
    this.freeRoam.set(false);
    this.adjustMapView();
  }

  private adjustMapView(): void {
    this.isProgrammaticChange = true;
    if (this.isOnlyOneMarkerPresent()) {
      this.map?.setZoom(17);
      this.map?.panTo(this.markers().at(0)!.position);
    } else {
      this.fitBounds();
    }
  }

  private fitBounds(): void {
    if (this.map) {
      this.mapBounds = new google.maps.LatLngBounds();
      const positionsToExpandBy: (
        google.maps.LatLngLiteral | google.maps.LatLng
      )[] = [
        ...this.svgs().flatMap((svg: Svg) => [svg.bounds.min, svg.bounds.max]),
        ...this.markers().map((marker: Marker) => marker.position),
        ...this.polylines().flatMap((polyline: Polyline) => polyline.positions),
        ...this.polygons().flatMap((polygon: Polygon) => polygon.path),
        ...(this.bounds() ? [this.bounds()!.min, this.bounds()!.max] : []),
      ];
      positionsToExpandBy.forEach(
        (position: google.maps.LatLngLiteral | google.maps.LatLng) => {
          this.mapBounds?.extend(position);
        }
      );
      this.map.fitBounds(this.mapBounds, this.getBoundsPadding());
    }
  }

  private getBoundsPadding(): number | google.maps.Padding {
    if (this.input().timeline) {
      return {
        top: 0,
        right: 0,
        left: 0,
        bottom: DEFAULT_TIMELINE_HEIGHT,
      };
    } else if (this.input().drawings?.markers) {
      const mapPadding = 8;
      return {
        top: mapPadding,
        right: 0,
        left: 0,
        bottom: mapPadding,
      };
    } else {
      return 0;
    }
  }

  /**
   * The single place that decides which SVG overlays are on the map, called on map init and
   * again after every layer or route-speed change. An overlay is built — and its markup parsed
   * — only the first time its `Svg` needs to be visible; after that it is attached or detached
   * with `setMap`. It replaces a "discard every overlay, then re-append from the source string"
   * pass that re-parsed the whole route on each toggle.
   */
  private syncSvgOverlays(): void {
    const map = this.map;
    if (!map) return;

    const svgs = this.svgs();

    /* The Map is keyed by object identity, so a new payload has to retire the old one's overlays. */
    this.svgOverlays.forEach((overlay: SvgOverlay, svg: Svg) => {
      if (!svgs.includes(svg)) {
        overlay.setMap(null);
        this.svgOverlays.delete(svg);
      }
    });

    svgs.forEach((svg: Svg) => {
      const shouldBeVisible =
        this.shouldIncludeSvg(svg) && this.isSvgLayerSelected(svg);
      const overlay = shouldBeVisible
        ? (this.svgOverlays.get(svg) ?? this.createSvgOverlay(svg))
        : this.svgOverlays.get(svg);

      /*
       * `getMap()` is the SDK's own record of attachment, so a sync that changes nothing stays a
       * no-op instead of bouncing the overlay through `onRemove`/`onAdd`.
       */
      if (overlay && !!overlay.getMap() !== shouldBeVisible) {
        overlay.setMap(shouldBeVisible ? map : null);
      }
    });
  }

  private createSvgOverlay(svg: Svg): SvgOverlay {
    const SvgOverlay = createSvgOverlayClass();
    const overlay = new SvgOverlay(
      new google.maps.LatLngBounds(svg.bounds.min, svg.bounds.max),
      svg.svg,
      this.svgMapPanesKey
    );
    this.svgOverlays.set(svg, overlay);

    return overlay;
  }

  /** A layerless svg, or a payload with no layer control at all, is always visible. */
  private isSvgLayerSelected(svg: Svg): boolean {
    return (
      !svg.layer ||
      !this.layers().length ||
      this.selectedLayers().includes(svg.layer)
    );
  }

  toggleRouteSpeedControl(isEnabled: boolean) {
    this.isRouteSpeedControlEnabled.set(isEnabled);

    this.syncSvgOverlays();
    this.updateRouteSpeedControlInUrl();
  }

  private shouldIncludeSvg(svg: Svg): boolean {
    if (!this.hasRouteSpeedControl()) {
      return true;
    }

    return (
      this.isRouteSpeedControlEnabled() === !!this.isSvgRouteColoredBySpeed(svg)
    );
  }

  private resetSvgs() {
    this.svgOverlays.forEach((overlay: SvgOverlay) => overlay.setMap(null));
    this.svgOverlays.clear();
  }

  private updateRouteSpeedControlInUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        [this.routeSpeedControlUrlParam]:
          this.isRouteSpeedControlEnabled() || null,
      },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Route-speed *state* only — restoring it from the URL used to go through
   * `toggleRouteSpeedControl()`, which meant building the SVG DOM a second time during init.
   * The overlays are built once afterwards, by `syncSvgOverlays()`.
   */
  private initRouteSpeedControlState(): void {
    if (!this.hasRouteSpeedControl()) return;

    this.isRouteSpeedControlEnabled.set(
      this.route.snapshot.queryParams[this.routeSpeedControlUrlParam] === 'true'
    );
    this.updateRouteSpeedControlInUrl();
  }

  private isSvgRouteColoredBySpeed(svg: Svg): boolean {
    return svg.metadata?.[this.isRouteColoredBySpeedPropertyName];
  }

  setMarkerFromTimeline(amLineChartItem: AmLineChartItem): void {
    this.markerFromTimeline.set({
      matsymbol: this.input().timeline?.amChart?.mapMarkerSymbol,
      position: amLineChartItem.position,
      label: this.datePipe.transform(new Date(amLineChartItem.date), 'HH:mm'),
    } as Marker<TInfo>);
    // No `detectChanges()`: `markerFromTimeline` is read in the template, so writing it already
    // marks this view dirty and schedules a tick. The v2 chart emits from inside an `effect()`,
    // i.e. mid-pass, and the removed call forced a synchronous re-render of the map subtree *and*
    // the timeline child that the normal pass then repeated. (`cdr` stays for the fullscreen
    // path, which deliberately flushes before measuring.)
  }

  onMapDrag(): void {
    this.freeRoam.set(true);
  }

  onIdle(): void {
    if (!this.map) return;
    this.scaleControlInput.set({
      zoom: this.map!.getZoom(),
      lat: this.map!.getCenter()?.lat(),
    });
  }

  addEventListeners(): void {
    ['bounds_changed', 'center_changed', 'zoom_changed'].forEach(
      (event: string) => {
        const listener = this.map!.addListener(event, () => {
          if (event === 'bounds_changed' && this.isProgrammaticChange) {
            this.isProgrammaticChange = false;
          } else if (
            event !== 'bounds_changed' &&
            !this.isProgrammaticChange &&
            !this.freeRoam()
          ) {
            this.freeRoam.set(true);
          }
        });
        this.mapEventListeners.push(listener);
      }
    );
  }

  toggleLayerSelectorDropdown() {
    this.isLayerSelectorDropdownOpen.update((value: boolean) => !value);
  }

  private updateOverlayContainerPosition() {
    this.cdr.detectChanges();
    if (this.isFullscreen()) {
      this.attachOverlayContainerToMap();
    } else {
      this.resetOverlayContainerToDefaultPosition();
    }
  }

  private isFullscreen(): boolean {
    return (
      (document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement) == this.mapContainer?.nativeElement
    );
  }

  private attachOverlayContainerToMap() {
    this.mapContainer?.nativeElement.appendChild(
      this.overlayContainer.getContainerElement()
    );
  }

  private resetOverlayContainerToDefaultPosition() {
    document.body.appendChild(this.overlayContainer.getContainerElement());
  }

  private getLayersControlPosition(): string | undefined {
    return Object.entries(this.mapControlPositions())
      .filter(([, controls]) => controls.includes('layers'))
      .map(([position]) => position)?.[0];
  }

  private switchTopAndBottomPositionPairs(): ConnectedPosition[] {
    const positions = [...defaultDropdownPositionPairs];
    const topPositions = positions.slice(0, 3);
    positions.splice(0, 3);
    positions.splice(3, 0, ...topPositions);
    return positions;
  }

  ngOnDestroy(): void {
    this.resetOverlayContainerToDefaultPosition();
    this.teardownMap();
  }

  private teardownMap(): void {
    this.mapEventListeners.forEach((listener) => listener.remove());
    this.mapEventListeners = [];

    // setMap(null) detaches each OverlayView and triggers its onRemove()
    this.resetSvgs();

    this.mapOverlay?.setMap(null);
    this.mapOverlay = undefined;

    if (this.map) {
      // The SDK retains every discarded Map instance for the life of the page
      // (TODO-94, unreleasable by design); clearing its instance listeners caps
      // what that retained Map drags along. One Map accrues per rendered map
      // view *and per map tile*, so a tile grid pays this several times a visit.
      //
      // The namespace is guarded separately from the map: `this.map` can be set
      // while `google` is not (an evicted SDK script, a chunk-load failure, a
      // spec that deleted its shim), and a `ReferenceError` here would skip the
      // detach below — the one line that keeps the retained Map from anchoring
      // the whole destroyed view subtree.
      if (typeof google !== 'undefined') {
        google.maps.event.clearInstanceListeners(this.map);
      }
      // Detach the SDK's own div as well: the page-lifetime Map holds it, and
      // via parentNode links it would otherwise anchor the entire destroyed
      // view subtree forever. Same two lines as
      // `BaseTrackingMapComponent.teardownMap`.
      this.map.getDiv().remove();
    }
  }
}
